import { computed, onBeforeUnmount, ref } from 'vue'
import {
  BRADLEY_VOICE_NOTE_MAX_BYTES,
  getBradleyErrorDescription,
  transcribeBradleyVoiceNote,
} from '../services/bradley-ai.service.js'
import { isBradleyAudioFormatSupported, useBradleyAudioRecorder } from './use-bradley-audio-recorder.js'

function isBrowserRecorderAvailable() {
  return Boolean(
    typeof navigator !== 'undefined'
    && navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== 'undefined'
    && isBradleyAudioFormatSupported(),
  )
}

const LIVE_WAVEFORM_STEP_MS = 50
const RECORDING_LIMIT_MS = 15 * 60 * 1000
const RECORDING_LIMIT_LABEL = '15:00'

function formatMinutesSeconds(milliseconds) {
  const totalSeconds = Math.max(0, Math.floor(Number(milliseconds) / 1000) || 0)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

async function createLiveAudioContext() {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  if (!AudioContextCtor) {
    return null
  }

  const context = new AudioContextCtor()
  if (context.state === 'suspended') {
    await context.resume()
  }
  return context
}

function detectAudioMime(bytes) {
  if (!bytes || bytes.length < 4) {
    return ''
  }

  if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return 'audio/ogg;codecs=opus'
  }

  if (bytes[0] === 0x1A && bytes[1] === 0x45 && bytes[2] === 0xDF && bytes[3] === 0xA3) {
    return 'audio/webm;codecs=opus'
  }

  if (
    bytes.length >= 8
    && bytes[4] === 0x66
    && bytes[5] === 0x74
    && bytes[6] === 0x79
    && bytes[7] === 0x70
  ) {
    return 'audio/mp4'
  }

  return ''
}

function fileNameForAudioType(fileName, mimeType) {
  if (!fileName) {
    return fileName
  }

  if (mimeType.includes('webm') && /\.ogg$/i.test(fileName)) {
    return fileName.replace(/\.ogg$/i, '.webm')
  }

  if (mimeType.includes('ogg') && /\.webm$/i.test(fileName)) {
    return fileName.replace(/\.webm$/i, '.ogg')
  }

  return fileName
}

async function materializePlaybackFile(file) {
  if (!file || typeof file.arrayBuffer !== 'function') {
    return file
  }

  try {
    const buffer = await file.arrayBuffer()
    const mimeType = detectAudioMime(new Uint8Array(buffer)) || file.type || 'audio/webm'
    return new File([buffer], fileNameForAudioType(file.name, mimeType), { type: mimeType })
  }
  catch {
    return file
  }
}

function isAbortError(error) {
  return error?.name === 'AbortError'
}

function isAuthenticationError(error) {
  return error?.name === 'AuthenticationError'
}

function transcriptionFallbackKey(error) {
  const status = Number(error?.status)
  if (status === 400 || status === 413) {
    return 'newBradley.input.transcriptionErrorRecordAgain'
  }
  if (status === 403 || status === 404) {
    return 'newBradley.input.transcriptionErrorInvalidConversation'
  }
  if (status === 503) {
    return 'newBradley.input.transcriptionErrorUnavailable'
  }
  return 'newBradley.input.transcriptionErrorLater'
}

export function useBradleyComposerRecorder({
  emit,
  resolveConversationId,
  onTranscriptReady,
} = {}) {
  const recorder = useBradleyAudioRecorder()
  const isInitializing = ref(false)
  const isRecording = ref(false)
  const isPaused = ref(false)
  const isTranscribing = ref(false)
  const displayedDurationMs = ref(0)
  const liveWaveform = ref([])
  let durationTimer = null
  let waveformTimer = null
  let liveAudio = null
  let waveformClock = 0
  let captureStream = null
  let captureContext = null
  let transcriptionAbort = null
  let transcriptionAbortedByUser = false

  const hasPreview = computed(() => (
    isRecording.value || isPaused.value || isTranscribing.value
  ))

  const waveformData = computed(() => liveWaveform.value)

  const formattedDuration = computed(() => (
    `${formatMinutesSeconds(displayedDurationMs.value)}/${RECORDING_LIMIT_LABEL}`
  ))

  function stopDurationTick() {
    if (durationTimer) {
      clearInterval(durationTimer)
      durationTimer = null
    }
  }

  function stopLiveWaveformLoop() {
    if (!waveformTimer) {
      return
    }

    cancelAnimationFrame(waveformTimer)
    waveformTimer = null
  }

  function stopLiveWaveform() {
    stopLiveWaveformLoop()

    if (!liveAudio) {
      return
    }

    liveAudio.source?.disconnect()
    if (liveAudio.context && liveAudio.context.state !== 'closed') {
      liveAudio.context.close().catch(() => {})
    }
    liveAudio = null
  }

  function releaseCapture() {
    stopLiveWaveform()

    captureStream?.getTracks?.().forEach(track => track.stop())
    captureStream = null

    if (captureContext && captureContext.state !== 'closed') {
      captureContext.close().catch(() => {})
    }
    captureContext = null
  }

  function sampleLiveWaveform() {
    if (!liveAudio?.analyser || !liveAudio.bins || isPaused.value) {
      return
    }

    if (liveAudio.context.state === 'suspended') {
      liveAudio.context.resume()
    }

    liveAudio.analyser.getByteFrequencyData(liveAudio.bins)

    let sum = 0
    for (const value of liveAudio.bins) {
      sum += value * value
    }

    const rms = Math.sqrt(sum / liveAudio.bins.length)
    const amplified = Math.min(255, rms * 2)
    const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now()
    const lastIndex = liveWaveform.value.length - 1
    const lastValue = lastIndex >= 0 ? liveWaveform.value[lastIndex] : null
    const shouldCommit = lastIndex < 0
      || lastValue < 0
      || (now - waveformClock) >= LIVE_WAVEFORM_STEP_MS

    if (shouldCommit) {
      liveWaveform.value.push(amplified)
      waveformClock = now
      return
    }

    liveWaveform.value[lastIndex] = amplified
  }

  function startLiveWaveformLoop({ sampleImmediately = true } = {}) {
    stopLiveWaveformLoop()

    const tick = () => {
      sampleLiveWaveform()
      waveformTimer = requestAnimationFrame(tick)
    }

    if (sampleImmediately) {
      sampleLiveWaveform()
    }

    waveformTimer = requestAnimationFrame(tick)
  }

  function startLiveWaveform(mediaStream, context) {
    stopLiveWaveform()
    liveWaveform.value = []
    waveformClock = 0

    if (!mediaStream || !context) {
      return
    }

    try {
      const analyser = context.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.45

      const source = context.createMediaStreamSource(mediaStream)
      source.connect(analyser)

      liveAudio = {
        context,
        analyser,
        source,
        bins: new Uint8Array(analyser.frequencyBinCount),
      }

      startLiveWaveformLoop()
    }
    catch {
      liveAudio = null
    }
  }

  function syncDisplayedDuration() {
    const elapsed = recorder.getRecordingDuration()
    displayedDurationMs.value = Math.min(elapsed, RECORDING_LIMIT_MS)
    return elapsed
  }

  function startDurationTick() {
    stopDurationTick()
    syncDisplayedDuration()
    durationTimer = setInterval(() => {
      if (syncDisplayedDuration() < RECORDING_LIMIT_MS) {
        return
      }

      stopDurationTick()
      stopRecording()
    }, 200)
  }

  function abortTranscription() {
    transcriptionAbortedByUser = true
    transcriptionAbort?.abort()
  }

  function resetTranscriptionRequest() {
    transcriptionAbort = null
    transcriptionAbortedByUser = false
  }

  function emitMicrophoneError(detailKey, detail) {
    emit?.('showToast', {
      severity: 'error',
      summaryKey: 'newBradley.input.microphoneError',
      detailKey,
      detail,
      life: 4000,
    })
  }

  function emitTranscriptionError(error) {
    if (isAuthenticationError(error)) {
      return
    }

    if (isAbortError(error) && transcriptionAbortedByUser) {
      return
    }

    const description = getBradleyErrorDescription(error)
    emit?.('showToast', {
      severity: 'error',
      summaryKey: 'newBradley.input.transcriptionFailed',
      detail: description || undefined,
      detailKey: description ? undefined : transcriptionFallbackKey(error),
      life: 5000,
    })
  }

  function emitSilentTranscriptNotice() {
    emit?.('showToast', {
      severity: 'warn',
      summaryKey: 'newBradley.input.transcriptionFailed',
      detailKey: 'newBradley.input.transcriptionSilent',
      life: 4000,
    })
  }

  async function transcribeAndSend(file, durationMs) {
    isTranscribing.value = true
    transcriptionAbortedByUser = false
    transcriptionAbort = new AbortController()

    try {
      const conversationId = await resolveConversationId?.('Voice note')
      if (transcriptionAbortedByUser) {
        return
      }

      if (!conversationId) {
        emit?.('showToast', {
          severity: 'error',
          summaryKey: 'newBradley.input.transcriptionFailed',
          detailKey: 'newBradley.input.transcriptionMissingConversation',
          life: 5000,
        })
        return
      }

      if (file.size > BRADLEY_VOICE_NOTE_MAX_BYTES) {
        emitTranscriptionError({ status: 400 })
        return
      }

      const result = await transcribeBradleyVoiceNote(conversationId, {
        file,
        file_name: file.name,
        duration_ms: durationMs,
      }, {
        signal: transcriptionAbort.signal,
      })

      if (transcriptionAbortedByUser) {
        return
      }

      const text = typeof result?.text === 'string' ? result.text : ''
      if (!text.trim()) {
        emitSilentTranscriptNotice()
        return
      }

      isTranscribing.value = false
      resetTranscriptionRequest()
      onTranscriptReady?.(text)
    }
    catch (error) {
      emitTranscriptionError(error)
    }
    finally {
      isTranscribing.value = false
      resetTranscriptionRequest()
    }
  }

  async function startRecording() {
    if (isInitializing.value || isRecording.value || isPaused.value || isTranscribing.value) {
      return
    }

    if (!isBrowserRecorderAvailable()) {
      emitMicrophoneError('newBradley.input.microphoneUnavailable')
      return
    }

    liveWaveform.value = []
    isInitializing.value = true
    displayedDurationMs.value = 0

    try {
      captureContext = await createLiveAudioContext()
      captureStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      })

      startLiveWaveform(captureStream, captureContext)
      recorder.startRecording(captureStream, { chunkInterval: 1000 })

      isRecording.value = true
      isPaused.value = false
      startDurationTick()
    }
    catch (error) {
      releaseCapture()
      isRecording.value = false
      isPaused.value = false
      const message = error instanceof Error ? error.message : ''
      emitMicrophoneError(undefined, message)
    }
    finally {
      isInitializing.value = false
    }
  }

  function pauseRecording() {
    if (!isRecording.value || isPaused.value) {
      return
    }

    recorder.pauseRecording()
    isPaused.value = true
    isRecording.value = true
    stopLiveWaveformLoop()
    stopDurationTick()
    syncDisplayedDuration()
    liveWaveform.value.push(-1)
  }

  function resumeRecording() {
    if (!isPaused.value) {
      return
    }

    recorder.resumeRecording()
    isPaused.value = false
    liveWaveform.value.push(-2)
    startLiveWaveformLoop({ sampleImmediately: false })
    startDurationTick()
  }

  async function stopRecording() {
    if (!isRecording.value && !isPaused.value) {
      return
    }

    stopDurationTick()
    const durationMs = syncDisplayedDuration()
    stopLiveWaveform()

    try {
      const file = await recorder.stopRecording()
      releaseCapture()
      isRecording.value = false
      isPaused.value = false
      liveWaveform.value = []

      if (!file || file.size <= 0) {
        emitTranscriptionError({ status: 400 })
        return
      }

      const uploadFile = await materializePlaybackFile(file)
      await transcribeAndSend(uploadFile, durationMs)
    }
    catch (error) {
      releaseCapture()
      isRecording.value = false
      isPaused.value = false
      liveWaveform.value = []
      const message = error instanceof Error ? error.message : ''
      emitMicrophoneError(undefined, message)
    }
  }

  async function toggleRecording() {
    if (isTranscribing.value) {
      return
    }

    if (isRecording.value || isPaused.value) {
      await stopRecording()
      return
    }

    await startRecording()
  }

  async function discardRecording() {
    if (isTranscribing.value) {
      abortTranscription()
      isTranscribing.value = false
    }

    if (isRecording.value || isPaused.value) {
      stopDurationTick()
      stopLiveWaveform()
      try {
        await recorder.stopRecording()
      }
      catch {}
      releaseCapture()
      isRecording.value = false
      isPaused.value = false
    }

    liveWaveform.value = []
    displayedDurationMs.value = 0
  }

  onBeforeUnmount(() => {
    abortTranscription()
    stopDurationTick()
    if (isRecording.value || isPaused.value) {
      recorder.stopRecording()
    }
    releaseCapture()
  })

  return {
    isInitializing,
    isRecording,
    isPaused,
    isTranscribing,
    displayedDurationMs,
    formattedDuration,
    waveformData,
    hasPreview,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    toggleRecording,
    discardRecording,
  }
}
